import { createAsyncThunk } from '@reduxjs/toolkit';
import fetchWeatherData from 'utils/fetchWeatherData';

/** How long the current weather is reused before the dashboard asks again. */
export const WEATHER_MAX_AGE_MS = 15 * 60 * 1000;

/** Whether the project says where it is: coordinates, or at least a city. */
export const hasWeatherLocation = (project) =>
  Boolean((project?.latitude && project?.longitude) || project?.city);

// The site itself when the project has coordinates (the weather API prefers a
// city it is given), its city otherwise.
const weatherLocationOf = (project) =>
  project.latitude && project.longitude
    ? {
        id: project.id,
        latitude: project.latitude,
        longitude: project.longitude,
      }
    : { id: project.id, city: project.city };

const numberOrNull = (value) =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/**
 * The current weather at the project's site, from the weather API the forms
 * already use (weatherapi.com's current conditions): the temperature (°C),
 * the air pressure (hPa) and the humidity (%), where the reading is from and
 * when it was taken. `current` is null when the project has no location or
 * the API gives no current conditions. Reused for WEATHER_MAX_AGE_MS.
 */
export const loadProjectWeather = createAsyncThunk(
  'projectOverview/loadWeather',
  async ({ project }) => {
    if (!hasWeatherLocation(project)) {
      return { loadedAt: Date.now(), current: null };
    }
    const data = await fetchWeatherData({
      project: weatherLocationOf(project),
      hours: [],
    });
    const current = data?.current;
    return {
      loadedAt: Date.now(),
      current: current
        ? {
            temperature: numberOrNull(current.temp_c),
            pressure: numberOrNull(current.pressure_mb),
            humidity: numberOrNull(current.humidity),
            place: data?.location?.name || null,
            updatedAt: current.last_updated || null,
          }
        : null,
    };
  },
  {
    // Once per project at a time, and not again while fresh.
    condition: ({ project, force }, { getState }) => {
      if (!project?.id) return false;
      const entry = getState().projectOverview?.weather?.[project.id];
      if (!entry) return true;
      if (entry.status === 'loading') return false;
      if (force) return true;
      return !(
        entry.status === 'succeeded' &&
        Date.now() - entry.loadedAt < WEATHER_MAX_AGE_MS
      );
    },
  }
);
